/**
 * Report Service for Majalisna Application
 * 
 * This service provides methods for generating and downloading reports.
 */

import { privateApiClient } from '../apiClient';
import API from '../../config/api';

const reportService = {
  /**
   * Download a nomination report
   * @param {number} nominationId - The ID of the nomination
   * @param {Object} options - Report options
   * @param {boolean} options.includeEvaluation - Whether to include evaluation
   * @param {boolean} options.includeAttachmentA - Whether to include attachment A
   * @param {boolean} options.includeAttachmentB - Whether to include attachment B
   * @param {string} options.format - Report format ('pdf' or 'zip')
   * @returns {Promise} - The axios promise with response type 'blob'
   */
  downloadNominationReport: (nominationId, options = {}) => {
    const {
      includeEvaluation = false,
      includeAttachmentA = false,
      includeAttachmentB = false,
      format = 'pdf'
    } = options;

    return privateApiClient.get(
      API.reports.nomination(nominationId),
      {
        params: {
          include_evaluation: includeEvaluation,
          include_attachment_a: includeAttachmentA,
          include_attachment_b: includeAttachmentB,
          format
        },
        responseType: 'blob',
        // Disable timeout to allow heavy server-side generation
        timeout: 0
      }
    );
  },

  /**
   * Bulk export reports for a branch or center
   * @param {Object} filters - Export filters
   * @param {string} filters.branchId - Branch CODE (e.g., 'jeddah', 'taif') (optional)
   * @param {number|string} filters.centerId - Center ID (numeric) (optional)
   * @param {string} filters.status - Nomination status (optional)
   * @param {Object} options - Report options
   * @param {boolean} options.includeEvaluation - Whether to include evaluations
   * @param {boolean} options.includeAttachments - Whether to include attachments
   * @param {string} options.format - Report format ('pdf' or 'zip')
   * @returns {Promise} - The axios promise with response type 'blob'
   */
  bulkExportReports: (filters = {}, options = {}) => {
    const {
      branchId,
      centerId,
      status
    } = filters;

    const {
      includeEvaluation = false,
      includeAttachments = false,
      format = 'zip'
    } = options;

    const params = {};
    if (branchId != null && String(branchId).trim()) params.branch_id = String(branchId).trim();
    if (centerId != null && String(centerId).trim()) params.center_id = String(centerId).trim();
    if (status) params.status = status;
    params.include_evaluation = includeEvaluation;
    params.include_attachments = includeAttachments;
    params.format = format;

    console.debug('[reportService] bulkExportReports params', params);
    return privateApiClient.get(API.reports.bulkExportReports, {
      params,
      responseType: 'blob',
      // Disable timeout to allow heavy server-side generation
      timeout: 0
    });
  }
};

export default reportService;
